import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import toast from 'react-hot-toast';
import { getUserActivities, deleteActivity, markActivityComplete } from '../../services/calendarApi';
import ActivityModal from './ActivityModal';
import ConfirmationModal from '../Common/ConfirmationModal';
import LoadingSpinner from '../Common/LoadingSpinner';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarView.css';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const CalendarView = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('month');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);

  // Fetch activities
  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getUserActivities();
      setActivities(response.data.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error(error.response?.data?.message || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Convert activities to calendar events
  const calendarEvents = activities.map(activity => {
    const startDate = new Date(activity.startDate);
    const endDate = new Date(activity.endDate);
    
    // For all-day events, ensure they display on a single day
    if (activity.allDay) {
      // Set to local midnight to avoid timezone issues
      const localStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const localEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      
      return {
        id: activity._id,
        title: activity.title,
        start: localStart,
        end: localEnd,
        resource: activity,
        allDay: true
      };
    }
    
    return {
      id: activity._id,
      title: activity.title,
      start: startDate,
      end: endDate,
      resource: activity,
      allDay: false
    };
  });

  // Event styling based on status and priority
  const eventStyleGetter = (event) => {
    const activity = event.resource;
    let backgroundColor = '#6366f1'; // indigo default
    
    switch(activity.status) {
      case 'completed':
        backgroundColor = '#10b981'; // green
        break;
      case 'in-progress':
        backgroundColor = '#f59e0b'; // amber
        break;
      case 'cancelled':
        backgroundColor = '#ef4444'; // red
        break;
      case 'scheduled':
        // Priority colors for scheduled
        if (activity.priority === 'urgent') backgroundColor = '#dc2626'; // red-600
        else if (activity.priority === 'high') backgroundColor = '#ea580c'; // orange-600
        else if (activity.priority === 'medium') backgroundColor = '#3b82f6'; // blue-600
        else backgroundColor = '#6b7280'; // gray-600
        break;
      default:
        backgroundColor = '#6366f1';
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: activity.status === 'completed' ? 0.6 : 0.9,
        color: 'white',
        border: '0',
        display: 'block'
      }
    };
  };

  // Handle event selection
  const handleSelectEvent = (event) => {
    setSelectedActivity(event.resource);
    setShowModal(true);
  };

  // Handle event slot selection (creating new activity)
  const handleSelectSlot = ({ start, end }) => {
    setSelectedActivity({
      startDate: start,
      endDate: end,
      status: 'scheduled',
      priority: 'medium',
      type: 'task'
    });
    setShowModal(true);
  };

  // Handle activity save
  const handleSaveActivity = () => {
    fetchActivities();
    setShowModal(false);
    setSelectedActivity(null);
  };

  // Handle activity delete
  const handleDeleteActivity = (activityId) => {
    setActivityToDelete(activityId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!activityToDelete) return;

    try {
      await deleteActivity(activityToDelete);
      toast.success('Activity deleted successfully');
      fetchActivities();
      setShowModal(false);
      setSelectedActivity(null);
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error(error.response?.data?.message || 'Failed to delete activity');
    } finally {
      setShowDeleteConfirm(false);
      setActivityToDelete(null);
    }
  };

  // Handle mark as complete
  const handleMarkComplete = async (activityId) => {
    try {
      await markActivityComplete(activityId);
      toast.success('Activity marked as complete');
      fetchActivities();
      setShowModal(false);
      setSelectedActivity(null);
    } catch (error) {
      console.error('Error marking activity complete:', error);
      toast.error(error.response?.data?.message || 'Failed to mark activity as complete');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading calendar..." />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Calendar</h2>
          <p className="text-sm text-gray-600 mt-1">Schedule and manage your activities</p>
        </div>
        <button
          onClick={() => {
            setSelectedActivity({
              startDate: new Date(),
              endDate: new Date(Date.now() + 60 * 60 * 1000), // +1 hour
              status: 'scheduled',
              priority: 'medium',
              type: 'task'
            });
            setShowModal(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Activity
        </button>
      </div>

      {/* Calendar Legend */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2 bg-red-600"></div>
            <span>Urgent</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2 bg-orange-600"></div>
            <span>High Priority</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2 bg-blue-600"></div>
            <span>Medium Priority</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2 bg-gray-600"></div>
            <span>Low Priority</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2 bg-amber-600"></div>
            <span>In Progress</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2 bg-green-600 opacity-60"></div>
            <span>Completed</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded mr-2 bg-red-600"></div>
            <span>Cancelled</span>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4" style={{ height: '700px' }}>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable
          date={calendarDate}
          onNavigate={setCalendarDate}
          view={calendarView}
          onView={setCalendarView}
          views={['month', 'week', 'day', 'agenda']}
          popup
          tooltipAccessor={(event) => {
            const activity = event.resource;
            return `${activity.title}\nType: ${activity.type}\nPriority: ${activity.priority}\nStatus: ${activity.status}`;
          }}
        />
      </div>

      {/* Activity Modal */}
      {showModal && (
        <ActivityModal
          activity={selectedActivity}
          onClose={() => {
            setShowModal(false);
            setSelectedActivity(null);
          }}
          onSave={handleSaveActivity}
          onDelete={handleDeleteActivity}
          onMarkComplete={handleMarkComplete}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setActivityToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Activity"
        message="Are you sure you want to delete this activity? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default CalendarView;
